import React from 'react';
import { KpiAmount } from '../shared/KpiAmount';

const fmtEuro = (n) => new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' }).format(Number(n || 0));

const SnapBadge = () => (
  <span className="text-[10px] font-semibold uppercase px-1.5 py-0.5 rounded bg-slate-200 text-slate-600 ml-2">attuale</span>
);

// KPI primario con dettaglio IVA (flussi di periodo: Incassato / Fatturato).
const PrimaryFlow = ({ label, bucket, countText }) => (
  <div className="rounded-lg border border-slate-200 bg-white p-4" data-testid={`kpi-primary-${label}`}>
    <div className="text-xs uppercase tracking-wide font-semibold text-[#525252] mb-1">{label}</div>
    <KpiAmount net={bucket?.net} vat={bucket?.vat} gross={bucket?.gross} vatRate={bucket?.vat_rate} variant="extended" />
    {countText != null && <div className="text-[11px] text-[#a3a3a3] mt-1">{countText}</div>}
  </div>
);

// §21-§22 — Residuo da incassare = Fatturato non incassato + Da fatturare + Non sbloccato.
const ResiduoCard = ({ fnf, daFatt, nonSbl, residuo }) => {
  const total = residuo > 0 ? residuo : 1;
  const segs = [
    { key: 'fnf', label: 'Fatturato non incassato', value: fnf, color: 'bg-blue-500', text: 'text-blue-700' },
    { key: 'daFatt', label: 'Da fatturare', value: daFatt, color: 'bg-orange-500', text: 'text-orange-700' },
    { key: 'nonSbl', label: 'Non sbloccato', value: nonSbl, color: 'bg-zinc-400', text: 'text-zinc-600' },
  ];
  return (
    <div className="rounded-lg border border-slate-200 bg-white p-4" data-testid="kpi-primary-Residuo da incassare">
      <div className="text-xs uppercase tracking-wide font-semibold text-[#525252] mb-1 flex items-center">
        Residuo da incassare<SnapBadge />
      </div>
      <div className="text-2xl font-bold text-[#3C3C3C] tabular-nums" data-testid="residuo-value">{fmtEuro(residuo)}</div>
      {/* micro-barra segmentata (§22) */}
      <div className="flex h-1.5 w-full rounded-full overflow-hidden mt-2 bg-slate-100" data-testid="residuo-bar">
        {segs.map((s) => (
          <div key={s.key} className={s.color} style={{ width: `${Math.max(0, (s.value / total) * 100)}%` }} title={`${s.label}: ${fmtEuro(s.value)}`} />
        ))}
      </div>
      {/* 3 valori inline */}
      <div className="mt-2 space-y-0.5">
        {segs.map((s) => (
          <div key={s.key} className="flex items-center justify-between text-[11px]" data-testid={`residuo-seg-${s.key}`}>
            <span className="flex items-center gap-1.5 text-[#737373]">
              <span className={`inline-block w-1.5 h-1.5 rounded-full ${s.color}`} />{s.label}
            </span>
            <span className={`font-semibold tabular-nums ${s.text}`}>{fmtEuro(s.value)}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

export const PaymentsKpiBar = ({ dashboard, year }) => {
  const iva = dashboard?.iva_kpi || {};
  const yk = dashboard?.year_kpi || {};
  const rec = dashboard?.receivables || {};

  const periodo = year === 'all' ? '· Tutti gli anni' : `nel ${year}`;
  const incCount = yk?.incassato?.count ?? 0;
  const fattCount = yk?.fatturato?.count ?? 0;
  const incText = `${incCount} ${incCount === 1 ? 'incasso' : 'incassi'} ${periodo}`;
  const fattText = `${fattCount} ${fattCount === 1 ? 'voce fatturata' : 'voci fatturate'} ${periodo}`;

  // PARITY (§42): Da fatturare / Fatturato-non-incassato (owed) dalla fonte canonica condivisa.
  const daFatt = rec?.da_fatturare_value ?? (iva?.da_fatturare?.net ?? 0);
  const fnf = rec?.da_incassare_value ?? (iva?.fatturato_non_incassato?.net ?? 0);
  const nonSbl = iva?.non_sbloccato?.net ?? 0;
  const residuo = Math.round((fnf + daFatt + nonSbl) * 100) / 100;

  const scaduto = iva?.scaduto || {};
  const verifiche = dashboard?.verifiche || { count: 0, iva_mancante: 0, non_associato: 0 };
  const scadutoVal = scaduto?.net ?? 0;
  const scadutoCnt = scaduto?.count ?? 0;
  const vCount = verifiche?.count ?? 0;
  const vIva = verifiche?.iva_mancante ?? 0;
  const vNonAssoc = verifiche?.non_associato ?? 0;
  const anomalie = scadutoCnt > 0 || vCount > 0;

  return (
    <div className="space-y-3" data-testid="payments-kpi-bar">
      {/* 3 KPI principali (§18) */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <PrimaryFlow label={`Incassato ${periodo}`} bucket={yk?.incassato} countText={incText} />
        <PrimaryFlow label={`Fatturato ${periodo}`} bucket={yk?.fatturato} countText={fattText} />
        <ResiduoCard fnf={fnf} daFatt={daFatt} nonSbl={nonSbl} residuo={residuo} />
      </div>

      {/* Fascia stato / verifiche (§24) */}
      <div className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2 flex items-center gap-4 flex-wrap text-xs" data-testid="payments-status-strip">
        {anomalie ? (
          <>
            {scadutoCnt > 0 && (
              <span className="flex items-center gap-1.5 text-rose-700 font-medium" data-testid="status-scaduto">
                <span className="inline-block w-1.5 h-1.5 rounded-full bg-rose-500" />
                Scaduto {fmtEuro(scadutoVal)} · {scadutoCnt} {scadutoCnt === 1 ? 'voce' : 'voci'}
                <span className="text-[#a3a3a3] font-normal">(solo voci con scadenza)</span>
              </span>
            )}
            {scadutoCnt === 0 && <span className="text-[#737373]" data-testid="status-scaduto">Scaduto 0,00 €</span>}
            {vCount > 0 && (
              <span className="flex items-center gap-1.5 text-amber-700 font-medium" data-testid="status-verifiche">
                <span className="inline-block w-1.5 h-1.5 rounded-full bg-amber-500" />
                {vCount} {vCount === 1 ? 'verifica amministrativa' : 'verifiche amministrative'}
                {vNonAssoc > 0 ? ` · ${vNonAssoc} ${vNonAssoc === 1 ? 'progetto non associato' : 'progetti non associati'}` : ''}
                {vIva > 0 ? ` · ${vIva} IVA mancante` : ''}
              </span>
            )}
          </>
        ) : (
          <span className="text-emerald-700 font-medium flex items-center gap-1.5" data-testid="status-ok">
            <span className="inline-block w-1.5 h-1.5 rounded-full bg-emerald-500" />Nessuna anomalia amministrativa
          </span>
        )}
      </div>

      <p className="text-[11px] text-[#737373] bg-slate-50 border border-slate-200 rounded px-2.5 py-1.5">
        Gli importi principali sono al netto IVA (imponibile). I KPI con <span className="font-semibold text-[#FF7819]">anno</span> sono flussi del periodo selezionato;
        il <span className="font-semibold text-slate-600">Residuo</span> è la situazione corrente (Fatturato non incassato + Da fatturare + Non sbloccato). “Scaduto” copre solo le voci con scadenza fattura disponibile (esclude i Lavori).
      </p>
    </div>
  );
};

export default PaymentsKpiBar;
